import AddressPicker from '../AddressPicker/AddressPicker';
import { useLocalStorage, useTracking } from '@components/hooks';
import Available from './Responses/Available/Available.js';
import NotAvailable from './Responses/NotAvailable/NotAvailable.js';
import React, { useState, useEffect } from 'react';
import AvailabilityCard from '../Availability/AvailabilityComponents/AvailabilityCard/AvailabilityCard';
import AvailabilityError from '../Availability/AvailabilityComponents/AvailabilityError/AvailabilityError';
import { defaultValuesHeat, getAddress, STEPS } from './HeatRecommender.config';
import PropTypes from 'prop-types';
import styles from './HeatRecommender';
import { Skeleton } from '@wienenergiegithub/ui-next';
import { defaultValuesAddressPicker } from '../AddressPicker/AddressPicker.config';

const HeatRecommender = ({ signUpLink, orderLink, heatResponses }) => {
  const [storage, setStorage] = useLocalStorage('address-check');
  const [heatStorage] = useLocalStorage('heat-info', defaultValuesHeat);
  const track = useTracking();
  const [step, setStep] = useState(STEPS.LOADING);

  // Update step based on Fernwaerme availability
  const setNewStep = (Fernwaerme, fernwaermeCase) => {
    switch (true) {
      case !Fernwaerme || !fernwaermeCase:
        setStep(STEPS.CHECK_AVAILABILITY);
        break;
      case Fernwaerme === 'JA' || fernwaermeCase === 'webGISVerfuegbar':
        setStep(STEPS.AVAILABLE);
        break;
      case fernwaermeCase === 'dataIssue':
        setStep(STEPS.DATA_ISSUE);
        break;
      case fernwaermeCase === 'heatError':
        setStep(STEPS.ERROR);
        break;
      case Boolean(fernwaermeCase):
        setStep(STEPS.NOT_AVAILABLE);
        break;
      default:
        setStep(STEPS.ERROR);
    }
  };

  // Set step on mount if there is sufficient info in storage
  useEffect(() => {
    setNewStep(storage?.Fernwaerme, heatStorage?.fernwaermeCase);
  }, []);

  // Handle successful address picker form submission
  const handleOnSuccess = (availabilities, setIsLoading, fernwaermeCase) => {
    if (!availabilities || !storage) return;

    const fernwaerme = availabilities.find(
      ({ sparte }) => sparte === 'Fernwaerme',
    );
    if (!fernwaerme) return;

    const isNotErrorCase =
      fernwaermeCase !== 'dataIssue' && fernwaermeCase !== 'heatError';
    const isAvailable =
      fernwaerme.verfuegbar === 'JA' || fernwaermeCase === 'webGisVerfuegbar';

    if (isNotErrorCase) {
      const zipCode = storage?.plzOrt?.split(' ')?.[0];

      if (isAvailable) {
        track({ event: 'fernwaerme_verfuegbar', options: { zipCode } });
      } else if (fernwaermeCase) {
        track({
          event: 'fernwaerme_nicht-verfuegbar',
          options: { zipCode, answer: fernwaermeCase },
        });
      }
    }

    setIsLoading(false);
    setNewStep(fernwaerme, fernwaermeCase);
  };

  const handleOnError = () => setStep(STEPS.ERROR);

  // Reset to default for a new address check
  const handleNewCheck = () => {
    setStep(STEPS.CHECK_AVAILABILITY);
    setStorage(defaultValuesAddressPicker);
  };

  return (
    <AvailabilityCard
      icon="thermometer-three-quarters"
      title="Ist Fernwärme bei mir verfügbar?"
    >
      {step === STEPS.LOADING && (
        <div className={styles.skeleton}>
          <Skeleton count={4} height="5.78rem" />
        </div>
      )}
      {step === STEPS.CHECK_AVAILABILITY && (
        <AddressPicker
          buttonId="fernwaerme-ueberpruefen"
          onError={handleOnError}
          onSuccess={handleOnSuccess}
          type="heat"
        />
      )}
      {step === STEPS.AVAILABLE && (
        <Available
          onNewCheck={handleNewCheck}
          orderLink={orderLink}
          signUpLink={signUpLink}
          storage={storage}
        />
      )}
      {
        // Render Error Widget if Data deliveres only olavkey
        step === STEPS.DATA_ISSUE && (
          <AvailabilityError
            buttonText="Weitere Adresse prüfen"
            content="Wir arbeiten laufend an der Erweiterung unserer Datenbank."
            heading="Für diese Adresse sind noch keine Daten vorhanden."
            onButtonClick={handleNewCheck}
            showButton
          />
        )
      }
      {step === STEPS.NOT_AVAILABLE && (
        <NotAvailable
          address={getAddress(storage)}
          heatResponses={heatResponses}
          onNewCheck={handleNewCheck}
          type="heat"
        />
      )}
      {step === STEPS.ERROR && (
        <AvailabilityError
          buttonText="Weitere Adresse prüfen"
          content="Bitte versuchen Sie es später noch einmal."
          heading="Es ist ein Fehler aufgetreten!"
          onButtonClick={handleNewCheck}
          showButton
        />
      )}
    </AvailabilityCard>
  );
};

HeatRecommender.propTypes = {
  // "Raus Aus Gas" responses from template settings
  heatResponses: PropTypes.arrayOf(
    PropTypes.shape({
      link: PropTypes.shape({
        url: PropTypes.string,
        title: PropTypes.string,
      }).isRequired,
      option: PropTypes.string,
      secondLink: PropTypes.shape({
        url: PropTypes.string,
        title: PropTypes.string,
        target: PropTypes.string,
      }),
      text: PropTypes.string,
      title: PropTypes.string,
    }),
  ).isRequired,
  // Link to order Fernwaerme
  orderLink: PropTypes.shape({
    url: PropTypes.string,
    title: PropTypes.string,
  }).isRequired,
  // Link to order Nachinstallation of Fernwaerme
  signUpLink: PropTypes.shape({
    url: PropTypes.string,
    title: PropTypes.string,
  }).isRequired,
};

export default HeatRecommender;
